import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import firebase from "services/firebase/firebase.config";
import { useAuthStore } from "stores/authStore/authStore";
import { COMPANY_AUTH_PRIMARY_BUTTON_STYLES } from "web-apps/company/components/shared/companyAuth.buttonStyles.contract";
import {
  Content,
  Description,
  FooterActions,
} from "./resetPasswordModal.styled";

type PasswordModalProps = {
  email: string;
  passwordModalOpen: boolean;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setPasswordModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ResetPasswordModal: React.FC<PasswordModalProps> = ({
  email,
  passwordModalOpen,
  setEmail,
  setPasswordModalOpen,
}) => {
  const [sent, setSent] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [authState, , authDispatch] = useAuthStore();
  const trimmedEmail = email.trim();
  const ableToSend = Boolean(trimmedEmail);

  const { t } = useTranslation();

  useEffect(() => {
    setEmailError(false);
  }, [email]);

  useEffect(() => {
    if (!passwordModalOpen) {
      setSent(false);
      setEmailError(false);
    }
  }, [passwordModalOpen]);

  const sendPasswordResetEmail = (): void => {
    if (!ableToSend || authState.isLoading) {
      return;
    }

    authDispatch({ type: "SET_IS_LOADING", payload: true });
    firebase
      .auth()
      .sendPasswordResetEmail(trimmedEmail)
      .then(() => {
        setSent(true);
        authDispatch({ type: "SET_IS_LOADING", payload: false });
      })
      .catch(() => {
        setEmailError(true);
        authDispatch({ type: "SET_IS_LOADING", payload: false });
      });
  };

  return (
    <Modal
      isOpen={passwordModalOpen}
      onClose={() => {
        setPasswordModalOpen(false);
      }}
      label={
        sent ? t("LoginCompany.Sent") : t("LoginCompany.ResetYourPassword")
      }
      size="compact"
      showCloseButton={false}
      footer={
        sent ? (
          <FooterActions>
            <PrimaryButton
              label={t("General.Ok")}
              onClick={() => setPasswordModalOpen(false)}
            />
          </FooterActions>
        ) : (
          <FooterActions>
            <SecondaryButton
              label={t("General.Cancel")}
              onClick={() => setPasswordModalOpen(false)}
            />
            <PrimaryButton
              label={t("LoginCompany.Submit")}
              disabled={!ableToSend || authState.isLoading}
              isLoading={authState.isLoading}
              onClick={sendPasswordResetEmail}
              styleOverrides={COMPANY_AUTH_PRIMARY_BUTTON_STYLES}
            />
          </FooterActions>
        )
      }
    >
      {!sent ? (
        <Content>
          <Description>{t("LoginCompany.EnterYourAccountEmail")}</Description>
          <InputField
            label={t("LoginCompany.Email")}
            type="email"
            value={email}
            placeholder={t("LoginCompany.Email")}
            fullWidth
            hasError={emailError}
            errorText={
              emailError ? t("LoginCompany.CouldNotVerify") : undefined
            }
            onChange={setEmail}
            onClear={() => setEmail("")}
          />
        </Content>
      ) : (
        <Content>
          <Description>{t("LoginCompany.CheckYourEmail")}</Description>
        </Content>
      )}
    </Modal>
  );
};
